import { NavLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { toggleNavCollapsed } from "@app/slices/prefsSlice";
import { useCapabilities } from "@components/catalog/useCapabilities";
import { CAPABILITIES } from "@api/capabilities";
import { useLocale } from "@hooks/useLocale";
import { TENANT_BRANDS } from "@oxbo/tokens";
import type { MessageKey } from "@i18n/messages";
import { WellSwitcher } from "./WellSwitcher";
import { SettingsMenu } from "./SettingsMenu";

/** Left nav mirroring the sitemap. Icons are simple glyphs (no icon dep). */
interface NavItem {
  to: string;
  labelKey: MessageKey;
  icon: string;
  /** Restrict to holders of this capability — omitted for everyone else (§ACL). */
  requiredCap?: string;
}

const ITEMS: NavItem[] = [
  { to: "/wells", labelKey: "nav.wellExplorer", icon: "⌖" },
  { to: "/well", labelKey: "nav.wellRecord", icon: "▤" },
  { to: "/daily", labelKey: "nav.dailyReport", icon: "☰" },
  { to: "/time-npt", labelKey: "nav.timeNpt", icon: "⏱" },
  { to: "/cost-afe", labelKey: "nav.costAfe", icon: "$" },
  { to: "/afe", labelKey: "nav.afe", icon: "▦" },
  { to: "/pad", labelKey: "nav.pad", icon: "▦" },
  { to: "/planner", labelKey: "nav.planner", icon: "▦" },
  { to: "/bha", labelKey: "nav.bha", icon: "⚙" },
  { to: "/tubulars", labelKey: "nav.tubulars", icon: "▮" },
  { to: "/mud", labelKey: "nav.mud", icon: "≋" },
  { to: "/hse", labelKey: "nav.hse", icon: "⛑" },
  { to: "/surveys", labelKey: "nav.surveys", icon: "∿" },
  { to: "/inbox", labelKey: "nav.inbox", icon: "✉" },
  { to: "/saved", labelKey: "nav.savedReports", icon: "★" },
  // Preview of the Oxbo-M catalog admin — gated on catalog:manage (§ACL). Users
  // without the capability never see it, so there is no path to the route.
  { to: "/catalog-admin", labelKey: "nav.catalogAdmin", icon: "▦", requiredCap: CAPABILITIES.catalogManage },
  // Access control (ACL) admin — gated on acl:manage (§ACL). Only designated
  // ACL managers see it; the screen + the grant/revoke endpoints self-gate too.
  { to: "/acl", labelKey: "nav.acl", icon: "🔑", requiredCap: CAPABILITIES.aclManage },
];

/**
 * Left nav — the full app chrome now the top header is gone. Hosts the tenant
 * wordmark, the well switcher, the sitemap links, a collapse/expand toggle, and
 * (pinned to the bottom) the Settings cogwheel. Collapsed = ~56px icon rail with
 * hover tooltips; expanded = ~220px icon + label. Collapsed state is a persisted
 * user preference.
 */
export function LeftNav() {
  const { t } = useLocale();
  const dispatch = useAppDispatch();
  const collapsed = useAppSelector((s) => s.prefs.navCollapsed);
  const tenant = useAppSelector((s) => s.prefs.tenant);
  const { can } = useCapabilities();
  const brand = TENANT_BRANDS[tenant];

  // Capability-gated entries drop out entirely for non-holders — no rendered
  // link, no route affordance (each target screen self-gates too, §ACL).
  const items = ITEMS.filter((item) => !item.requiredCap || can(item.requiredCap));

  return (
    <nav
      className={`leftnav${collapsed ? " collapsed" : ""}`}
      aria-label="Sections"
      data-collapsed={collapsed}
    >
      <div className="leftnav-top">
        <div className="leftnav-brand-row">
          <div className="wordmark" title={collapsed ? brand.wordmark : undefined}>
            <span className="mark" aria-hidden="true">
              {brand.initial}
            </span>
            {!collapsed && <span className="wordmark-text">{brand.wordmark}</span>}
          </div>
          {/* Help — icon only, pinned to the right of the wordmark (§Ramon). */}
          <a
            className="leftnav-help-top"
            href="/ayuda/reportes-workover.html"
            target="_blank"
            rel="noopener"
            title={t("nav.help")}
            aria-label={t("nav.help")}
          >
            <span aria-hidden="true">?</span>
          </a>
        </div>

        <WellSwitcher collapsed={collapsed} />
      </div>

      <div className="leftnav-links">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => (isActive ? "active" : "")}
            title={collapsed ? t(item.labelKey) : undefined}
          >
            <span className="nav-icon" aria-hidden="true">
              {item.icon}
            </span>
            <span className="nav-label">{t(item.labelKey)}</span>
            {collapsed && <span className="nav-tooltip" role="tooltip">{t(item.labelKey)}</span>}
          </NavLink>
        ))}
      </div>

      <div className="leftnav-bottom">
        <button
          type="button"
          className="nav-collapse-toggle"
          aria-expanded={!collapsed}
          aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}
          title={collapsed ? t("nav.expand") : t("nav.collapse")}
          onClick={() => dispatch(toggleNavCollapsed())}
        >
          <span className="chev" aria-hidden="true">
            {collapsed ? "»" : "«"}
          </span>
          {!collapsed && <span className="nav-label">{t("nav.collapse")}</span>}
        </button>

        <SettingsMenu collapsed={collapsed} />

        {!collapsed && (
          <div className="app-version" title={t("nav.version")}>
            {import.meta.env.VITE_APP_VERSION ?? "dev"}
          </div>
        )}
      </div>
    </nav>
  );
}
